// src/InventoryPage.js
import React, { useState, useEffect, useMemo, useCallback } from 'react';
// --- MODIFIED LINE: Import withdrawItems ---
import { getUserInventory, withdrawItems } from './api';
import { useAuth } from './AuthContext';
import { Link as RouterLink } from 'react-router-dom'; // <-- Import RouterLink

// --- Material UI Imports ---
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardMedia from '@mui/material/CardMedia';
import Tooltip from '@mui/material/Tooltip';
import Chip from '@mui/material/Chip';
import Pagination from '@mui/material/Pagination';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button'; // <-- Import Button
import CheckCircleIcon from '@mui/icons-material/CheckCircle'; // <-- Icon for selection
import Link from '@mui/material/Link'; // <-- Import Link for profile link
import { styled, alpha } from '@mui/system';

// --- Constants ---
const ITEMS_PER_PAGE = 48;
const PLACEHOLDER_BG_COLOR = (theme) => alpha(theme.palette.background.paper, 0.5);
const STEAM_IMAGE_SIZE_SUFFIX = '/96fx96f?allow_animated=1';
const GRID_BORDER_COLOR = (theme) => alpha(theme.palette.divider, 0.4);
const SELECTED_BORDER_COLOR = (theme) => theme.palette.success.main; // Color for selected item border
const SELECTED_OVERLAY_COLOR = alpha('#2e7d32', 0.3); // Overlay for selected item

// Helper function to format date (no changes)
const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
        return new Date(dateString).toLocaleString();
    } catch (e) {
        console.error("Error formatting date:", e);
        return 'Invalid Date';
    }
};

// Helper function to format wear (no changes)
const formatWear = (wearValue) => {
    if (wearValue === null || wearValue === undefined || wearValue === '') {
        return 'N/A';
    }
    if (typeof wearValue === 'number') {
        return wearValue.toFixed(6);
    }
    return String(wearValue);
};

// Helper function to get Steam community image URL (no changes)
const getSteamImageUrl = (iconUrlPath) => {
    if (!iconUrlPath) return '';
    if (iconUrlPath.startsWith('http')) {
        if (iconUrlPath.includes('community.cloudflare.steamstatic.com/economy/image/') && !iconUrlPath.match(/\/\d+fx\d+f/)) {
            const separator = iconUrlPath.includes('?') ? '&' : '?';
            if (iconUrlPath.match(/\/\d+fx\d+f/) && !iconUrlPath.includes('allow_animated=1')) {
                return `${iconUrlPath}${separator}allow_animated=1`;
            }
            return `${iconUrlPath}${STEAM_IMAGE_SIZE_SUFFIX}`;
        }
        return iconUrlPath;
    }
    if (iconUrlPath.startsWith('//')) {
        return `https:${iconUrlPath}${STEAM_IMAGE_SIZE_SUFFIX}`;
    }
    if (iconUrlPath.startsWith('ttps://')) {
        return `h${iconUrlPath}${STEAM_IMAGE_SIZE_SUFFIX}`;
    }
    return `https://community.cloudflare.steamstatic.com/economy/image/${iconUrlPath}${STEAM_IMAGE_SIZE_SUFFIX}`;
};


function InventoryPage() {
    const [inventory, setInventory] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const { user } = useAuth();
    const [currentPage, setCurrentPage] = useState(1);

    // --- NEW STATE ---
    const [selectedItems, setSelectedItems] = useState(new Set()); // Store selected originalAssetIds (as strings)
    const [isWithdrawing, setIsWithdrawing] = useState(false);
    const [withdrawSuccessMessage, setWithdrawSuccessMessage] = useState('');
    const [withdrawErrorMessage, setWithdrawErrorMessage] = useState('');
    // --- END NEW STATE ---

    // Set Document Title
    useEffect(() => {
        document.title = 'SpinnerCS - Inventory';
    }, []);

    // --- MODIFIED: Wrap fetch in useCallback for potential reuse ---
    const fetchInventory = useCallback(async () => {
        if (user?.isAuthenticated) {
            setLoading(true);
            setError(null);
            // Clear withdrawal messages on refresh
            setWithdrawSuccessMessage('');
            setWithdrawErrorMessage('');
            try {
                const data = await getUserInventory();
                const sortedData = [...(data || [])].sort((a, b) =>
                    new Date(b.acquiredDate) - new Date(a.acquiredDate)
                );
                setInventory(sortedData);
                // Clear selection after fetching new data
                setSelectedItems(new Set());
            } catch (err) {
                setError('Failed to load inventory. Please try again later.');
                console.error("Inventory fetch error:", err.response?.data || err.message || err);
            } finally {
                setLoading(false);
            }
        } else {
            setInventory([]);
            setSelectedItems(new Set()); // Clear selection if user logs out
        }
    }, [user]); // Dependency: user

    // Initial fetch and refetch on user change
    useEffect(() => {
        fetchInventory();
    }, [fetchInventory]); // Dependency: fetchInventory callback

    // Calculate items for the current page
    const itemsOnPage = useMemo(() => {
        const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
        const endIndex = startIndex + ITEMS_PER_PAGE;
        return inventory.slice(startIndex, endIndex);
    }, [inventory, currentPage]);

    // Calculate total page count
    const pageCount = useMemo(() => {
        return Math.ceil(inventory.length / ITEMS_PER_PAGE);
    }, [inventory]);

    // Handle page change
    const handlePageChange = (event, value) => {
        setCurrentPage(value);
        window.scrollTo(0, 0);
    };

    // --- MODIFIED: Handle Item Click for Selection using originalAssetId ---
    const handleItemClick = (item) => {
        // Use originalAssetId
        const assetId = item.originalAssetId;

        // Convert to string as the API expects strings and Set comparison works best with strings
        const assetIdStr = String(assetId);

        if (!assetIdStr || item.isWithdrawn) {
            // Do not allow selecting items without originalAssetId or already withdrawn
            return;
        }

        setSelectedItems(prevSelected => {
            const newSelected = new Set(prevSelected);
            // Use the string version for Set operations
            if (newSelected.has(assetIdStr)) {
                newSelected.delete(assetIdStr);
            } else {
                newSelected.add(assetIdStr);
            }
            return newSelected;
        });
        // Clear messages when selection changes
        setWithdrawSuccessMessage('');
        setWithdrawErrorMessage('');
    };
    // --- END MODIFIED ---

    // --- NEW: Handle Withdraw Action ---
    const handleWithdraw = async () => {
        if (selectedItems.size === 0 || isWithdrawing) {
            return;
        }

        setIsWithdrawing(true);
        setWithdrawSuccessMessage('');
        setWithdrawErrorMessage('');

        // The selectedItems Set already contains the originalAssetIds as strings
        const assetIdsToWithdraw = Array.from(selectedItems);

        try {
            // Pass the array of originalAssetId strings to the API
            const result = await withdrawItems(assetIdsToWithdraw);
            // Handle success (even partial) - API returns 200 OK
            setWithdrawSuccessMessage(result.message || 'Withdrawal request processed.');

            // Optionally display more details about failures if needed
            if (result.failedItems && result.failedItems.length > 0) {
                // Ensure failed item IDs are also strings for comparison if needed later
                const failedReasons = result.failedItems.map(f => `Item ${String(f.assetId)}: ${f.reason}`).join('; ');
                setWithdrawErrorMessage(`Some items failed: ${failedReasons}`);
            }

            // Refresh inventory to show updated status (isWithdrawn)
            // This also clears the selection automatically due to fetchInventory logic
            await fetchInventory();

        } catch (error) {
            // Handle API errors (4xx, 5xx)
            let specificError = 'An unexpected error occurred during withdrawal.';
            if (error.response) {
                // Use backend message if available
                specificError = error.response.data?.message || `Error ${error.response.status}: ${error.response.statusText}`;
                // Special case for missing trade URL
                if (error.response.status === 400 && specificError.toLowerCase().includes('trade url')) {
                    specificError = (
                        <>
                            Steam Trade URL is required or invalid. Please{' '}
                            <Link component={RouterLink} to="/profile" underline="hover">
                                set your Trade URL in your profile
                            </Link>
                            {' '}before withdrawing.
                        </>
                    );
                }
            } else if (error.message) {
                specificError = error.message;
            }
            setWithdrawErrorMessage(specificError);
            console.error("Withdrawal failed:", error.response || error);
        } finally {
            setIsWithdrawing(false);
        }
    };
    // --- END NEW ---


    // Loading/Error/Auth States
    if (!user?.isAuthenticated) {
        return <Alert severity="info" sx={{ mt: 4 }}>Please log in to see your inventory.</Alert>;
    }

    if (loading && inventory.length === 0) { // Show loading only on initial load
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 4, minHeight: '200px' }}>
                <CircularProgress />
                <Typography sx={{ ml: 2 }}>Loading inventory...</Typography>
            </Box>
        );
    }

    if (error && inventory.length === 0) { // Show error only if inventory couldn't be loaded at all
        return <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>;
    }

    // Tooltip Title generation (no changes needed here)
    const getTooltipTitle = (item) => {
        if (!item) return '';
        const name = item.name || 'Unnamed Item';
        const date = formatDate(item.acquiredDate);
        const wear = formatWear(item.wear);

        return (
            <React.Fragment>
                <Typography color="inherit" sx={{ fontWeight: 'bold' }}>{name}</Typography>
                {wear !== 'N/A' && (
                    <Typography variant="caption" display="block">Wear: {wear}</Typography>
                )}
                <Typography variant="caption" display="block">Acquired: {date}</Typography>
                {item.isWithdrawn && (
                    <Typography variant="caption" display="block" sx={{ color: 'warning.light', fontWeight: 'bold' }}>Status: Withdrawn</Typography>
                )}
            </React.Fragment>
        );
    };

    return (
        <Box sx={{ mt: 2, px: { xs: 1, sm: 2 } }}>
            <Typography variant="h4" gutterBottom align="center" sx={{ mb: 3 }}>
                Your Inventory ({inventory.length} items)
            </Typography>

            {/* --- NEW: Disclaimer --- */}
            <Alert severity="warning" sx={{ mb: 3, justifyContent: 'center' }}>
                <strong>Important:</strong> Items not withdrawn from your inventory within
                <strong> 7 days</strong> of being won may be automatically removed to ensure bot inventory capacity.
                Please withdraw your items promptly.
            </Alert>
            {/* --- END NEW: Disclaimer --- */}
            {/* --- Withdrawal Controls & Feedback --- */}
            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                justifyContent="center"
                alignItems="center"
                sx={{ mb: 3 }}
            >
                <Button
                    variant="contained"
                    color="success"
                    onClick={handleWithdraw}
                    disabled={selectedItems.size === 0 || isWithdrawing || loading} // Disable if nothing selected, withdrawing, or loading inventory
                    startIcon={isWithdrawing ? <CircularProgress size={20} color="inherit" /> : null}
                    sx={{ minWidth: '220px' }}
                >
                    {isWithdrawing ? 'Processing...' : `Withdraw Selected (${selectedItems.size})`}
                </Button>
                {loading && !isWithdrawing && <CircularProgress size={24} titleAccess="Refreshing inventory..." />}
            </Stack>
            <Box sx={{ mb: 3, textAlign: 'center', minHeight: '40px' }}> {/* Reserve space for messages */}
                {withdrawSuccessMessage && <Alert severity="success" variant="filled">{withdrawSuccessMessage}</Alert>}
                {withdrawErrorMessage && <Alert severity="error" variant="filled">{withdrawErrorMessage}</Alert>}
                {/* Display general fetch error if it occurs after initial load */}
                {error && inventory.length > 0 && <Alert severity="warning" sx={{ mt: 1 }}>{error}</Alert>}
            </Box>
            {/* --- End Withdrawal Controls --- */}


            {/* Grid container */}
            <Grid container spacing={{ xs: 0.5, sm: 1 }}>
                {itemsOnPage.map((item) => {
                    // --- MODIFIED: Check selection using originalAssetId (as string) ---
                    const isSelected = selectedItems.has(String(item.originalAssetId));
                    // --- MODIFIED: Check selectability using originalAssetId ---
                    const canSelect = !item.isWithdrawn && item.originalAssetId;
                    // --- END MODIFIED ---

                    return (
                        <Grid
                            item
                            xs={3} sm={2} md={1.5} lg={1.5}
                            // --- MODIFIED: Use originalAssetId for key ---
                            key={item.originalAssetId || item.userInventoryItemId}
                            sx={{
                                border: theme => `1px solid ${GRID_BORDER_COLOR(theme)}`,
                                boxSizing: 'border-box',
                            }}
                        >
                            <Tooltip title={getTooltipTitle(item)} placement="top" arrow>
                                <Card
                                    onClick={canSelect ? () => handleItemClick(item) : undefined} // Add onClick handler
                                    sx={(theme) => ({
                                        width: '100%',
                                        aspectRatio: '1 / 1',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        boxSizing: 'border-box',
                                        opacity: item.isWithdrawn ? 0.55 : 1, // Slightly more faded if withdrawn
                                        // --- MODIFIED: Conditional Styling ---
                                        border: isSelected
                                            ? `3px solid ${SELECTED_BORDER_COLOR(theme)}` // Thick border if selected
                                            : item.isWithdrawn
                                                ? `2px solid ${alpha('#FFA500', 0.8)}` // Use a distinct color for withdrawn, e.g., orange
                                                : item.nameColor
                                                    ? `2px solid #${item.nameColor}`
                                                    : 'none', // No border if not selected, withdrawn, or colored
                                        cursor: canSelect ? 'pointer' : 'not-allowed', // Change cursor based on selectability
                                        // --- END MODIFIED ---
                                        position: 'relative',
                                        backgroundColor: alpha(theme.palette.background.paper, 0.6),
                                        overflow: 'hidden',
                                        boxShadow: isSelected ? 4 : 1, // More shadow if selected
                                        transition: 'transform 0.15s ease-in-out, box-shadow 0.15s ease-in-out, border-color 0.15s ease-in-out',
                                        '&:hover': canSelect ? { // Only apply hover effect if selectable
                                            transform: 'scale(1.04)',
                                            boxShadow: isSelected ? 6 : 3,
                                            zIndex: 2,
                                        } : {},
                                        // --- NEW: Selection Overlay ---
                                        '&::after': isSelected ? {
                                            content: '""',
                                            position: 'absolute',
                                            top: 0, left: 0, right: 0, bottom: 0,
                                            backgroundColor: SELECTED_OVERLAY_COLOR,
                                            pointerEvents: 'none', // Allow clicks through overlay
                                        } : {},
                                        // --- END NEW ---
                                    })}
                                >
                                    {/* --- NEW: Selection Checkmark --- */}
                                    {isSelected && (
                                        <CheckCircleIcon
                                            sx={{
                                                position: 'absolute',
                                                top: 4,
                                                left: 4,
                                                color: 'white', // White icon on green overlay
                                                fontSize: '1.3rem',
                                                zIndex: 1,
                                                filter: 'drop-shadow(1px 1px 1px rgba(0,0,0,0.7))'
                                            }}
                                        />
                                    )}
                                    {/* --- END NEW --- */}

                                    {/* Withdrawn Chip (Keep as is) */}
                                    {item.isWithdrawn && (
                                        <Chip
                                            label="W"
                                            title="Withdrawn"
                                            color="warning"
                                            size="small"
                                            sx={{
                                                position: 'absolute', top: 2, right: 2, zIndex: 1,
                                                fontSize: '0.5rem', height: '14px', lineHeight: '14px', fontWeight: 'bold',
                                                '& .MuiChip-label': { px: '4px', py: '0px' }, opacity: 0.9
                                            }}
                                        />
                                    )}
                                    {/* Item Image (Keep as is) */}
                                    <CardMedia
                                        component="img"
                                        sx={{
                                            width: '80%',
                                            maxHeight: '80%',
                                            objectFit: 'contain',
                                            opacity: isSelected ? 0.85 : 1, // Slightly dim image when selected
                                        }}
                                        image={getSteamImageUrl(item.iconUrl)}
                                        alt={item.name || 'Inventory item'}
                                        onError={(e) => { e.target.style.display = 'none'; }}
                                    />
                                    {/* Fallback for missing image (Keep as is) */}
                                    {!item.iconUrl && (
                                        <Box sx={{
                                            width: '80%',
                                            height: '80%',
                                            display: 'flex',
                                            alignItems: 'center', justifyContent: 'center', color: 'text.secondary',
                                        }}>
                                            <Typography variant="h6" sx={{ fontSize: '2rem' }}>?</Typography>
                                        </Box>
                                    )}
                                </Card>
                            </Tooltip>
                        </Grid>
                    );
                })}
            </Grid>

            {/* Pagination Controls */}
            {pageCount > 1 && (
                <Stack spacing={2} sx={{ mt: 3, mb: 2, alignItems: 'center' }}>
                    <Pagination
                        count={pageCount}
                        page={currentPage}
                        onChange={handlePageChange}
                        color="primary"
                        showFirstButton
                        showLastButton
                        disabled={isWithdrawing || loading} // Disable pagination during actions
                    />
                </Stack>
            )}

            {/* Message if inventory is completely empty */}
            {inventory.length === 0 && !loading && !error && ( // Added !error condition
                <Alert severity="info" sx={{ mt: 4, justifyContent: 'center' }}>
                    Your inventory is empty. Spin the wheel or complete offers to win items!
                </Alert>
            )}
        </Box>
    );
}

export default InventoryPage;
